import {EffectRef, Injectable, Signal, WritableSignal, effect, inject, signal} from '@angular/core';
import {BehaviorSubject, Observable, take} from "rxjs";
import {tap, map, catchError, of} from "rxjs";
import {ApiService} from "./api.service";
import {TYPE} from "../types/types";
import {TokenService} from "./token.service";

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  private apiService: ApiService = inject(ApiService);
  private tokenService: TokenService = inject(TokenService);

  private language: WritableSignal<string> = signal('de');
  private languageDefault: string = (navigator.language && navigator.language.substring(0, 2) !== 'de') ? 'en' : 'de';
  private languages: TYPE.Languages = {};
  private loadingIndicatorSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public loadingIndicator$: Observable<number> = this.loadingIndicatorSubject.asObservable();
  private loadingLanguages: Set<string> = new Set<string>();
  private latestSetLanguageRequestId: number = 0;

  public language$: Signal<string> = this.language.asReadonly();

  private tokenStateEffect: EffectRef = effect(() => {
    const tokenState = this.tokenService.tokenState();
    if (tokenState === 'set' || tokenState === 'loaded') {
      this.loadTextBlocks(this.getLanguage()).pipe(take(1)).subscribe();
    }
  });

  public setLanguage(language: string): void {
    if (language) {
      const requestedLanguage = language.toLowerCase();
      const requestId = ++this.latestSetLanguageRequestId;
      this.language.set(requestedLanguage);
      this.loadingIndicatorSubject.next(1);

      this.loadTextBlocks(requestedLanguage)
      .pipe(take(1))
      .subscribe(() => {
        if (requestId === this.latestSetLanguageRequestId) {
          this.loadingIndicatorSubject.next(0);
        }
      });
    }
  }

  public setLanguageDefault(): void {
    this.language.set(this.languageDefault);
    this.loadTextBlocks().pipe(take(1)).subscribe();
  }

  public getLanguage(): string {
    return this.language();
  }

  public getLanguages(): TYPE.Languages {
    return this.languages;
  }

  public hasLoadedLanguage(language: string): boolean {
    return !!this.languages[language.toLowerCase()];
  }

  public getTranslationByKey(key: string): string {
    try {
      if(this.hasLanguage()) {
        if (this.languages[this.language()][key]) {
          return this.languages[this.language()][key];
        } else {
          return key;
        }
      } else {
        if (this.languages['en'][key]) {
          return this.languages['en'][key];
        } else {
          return key;
        }
      }
      
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) { /* empty */ }
    return key;
  }

  private addLanguage(language: string, translations: Record<string, string>): void {
    this.languages[language.toLowerCase()] = translations;
  }

  public hasLanguageKey(key: string): boolean {
    if(!this.hasLanguage()) {
      return false;
    }
    return !!this.languages[this.language()][key];
  }

  private hasLanguage() : boolean {
    return this.languages[this.language()] === undefined ? false : true;
  }

  public init(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.loadTextBlocks(this.getLanguage())
      .pipe(take(1))
      .subscribe({
        next: (success) => resolve(success),
        error: () => resolve(false)
      });
    });
  }

  private loadTextBlocks(language: string = this.languageDefault, forceReload: boolean = false): Observable<boolean> {
    const lang = language.toLowerCase();
    // If already loaded, return Observable of true
    if (!forceReload && this.languages[lang]) {
      return of(true);
    }
    // If currently loading, return Observable that completes when loading is done
    if (this.loadingLanguages.has(lang)) {
      return new Observable<boolean>((observer) => {
        const checkLoaded = () => {
          if (this.languages[lang]) {
            observer.next(true);
            observer.complete();
            clearInterval(interval);
          }
        };
        const interval = setInterval(checkLoaded, 50);
        // Timeout after 10 seconds
        setTimeout(() => {
          clearInterval(interval);
          observer.next(!!this.languages[lang]);
          observer.complete();
        }, 10000);
      });
    }
    this.loadingLanguages.add(lang);
    return this.apiService.getTextBlocks(lang)
      .pipe(
        take(1),
        tap((result: Record<string, string>) => {
          this.addLanguage(lang, result);
          this.loadingLanguages.delete(lang);
        }),
        map(() => true),
        catchError((error: unknown) => {
          console.error(error);
          this.loadingLanguages.delete(lang);
          return of(false);
        })
      );
  }
}
